/**
 * The permission matrix from BUILD_SPEC section 2, in one place, so the API's
 * enforcement and the UI's chrome cannot drift apart.
 *
 * The API imports this to **enforce**. The web app imports it to **hide**.
 * Hiding is not security: every route re-checks, because a hidden button is
 * still a reachable endpoint.
 *
 * Three rows in the source matrix are not expressible as booleans and are
 * handled where they are enforced, not here:
 *
 * - "own WO entries" / "assigned WOs" / "own requests only" are **row-level**
 *   scopes. `capabilitiesFor` returns the capability; the route additionally
 *   filters by ownership. `scopeFor` names which scope applies.
 * - The `vendor` column is aspirational. There is no `vendor` role in
 *   `user_role`, no vendor identity on `app_user`, and no grant table (B3), so
 *   every vendor ✓ is unreachable and no vendor row appears below.
 * - DECISION B1 — whether `viewer` (dispatch) reads `internal_only` comments —
 *   is a live product decision. It is a single named constant rather than a
 *   value buried in a table, so changing it is one edit with one place to test.
 */
import type { UserRole } from "./vocabulary.js";
import type { Capabilities } from "./wire.js";
import { visibleNavigationItems } from "./navigation.js";

/**
 * DECISION B1, pending Eden's call. Currently **denied**.
 *
 * Denying it keeps the `internal_only` tier meaningful, at the documented cost:
 * the status board's "last update" line has almost nothing to quote, because
 * every comment defaults to internal. So the board quotes **stage changes**
 * instead of comment bodies — which is honest under either resolution and does
 * not have to be rewritten when the decision lands.
 *
 * Flip to `true` and the board gains comment text and `viewer` gains internal
 * reads, together. Do not flip one without the other.
 */
export const VIEWER_READS_INTERNAL_COMMENTS = false;

/*
 * `searchComments` and `FULL_TEXT_SEARCH_ENABLED` were removed here -- TRBUD rider 4.2.
 *
 * The capability was false for every role and read by NOTHING for its whole life.
 * It is not that the feature was never built: it shipped under a different gate.
 * `searchPredicate` in `apps/api/src/read/work-orders.ts` searches the unfiltered
 * `v_work_order_search.search_text` blob -- the exact thing this flag existed to
 * withhold -- and gates it on `seeInternalComments`, which is the role set allowed
 * to read the whole blob anyway. TRBUD-240 then added comment-BODY matching
 * beside it, carrying the same visibility predicate `activityLateral` uses.
 *
 * So B2's constraint is satisfied and is enforced where the query is, not by a
 * boolean two packages away. Keeping a dead flag beside a live gate is how the
 * two come to disagree: the next reader flips the flag expecting it to matter.
 * The reasoning now lives on `searchPredicate`, next to the code it constrains.
 */


/** Which rows a capability applies to, when it is not "all of them". */
export type RowScope = "all" | "assigned" | "own" | "none";

const ALL: UserRole[] = ["admin", "manager", "mechanic", "processor", "requester", "viewer"];

/**
 * Management. **`processor` is one of these** (Eden, 14 August 2026: "the
 * coordinators are management/admin").
 *
 * BUILD_SPEC section 2 grants the managerial rows to `admin` and `manager` only,
 * but that matrix was written against a five-role vocabulary with no `processor`
 * — it mapped Matt onto `manager` instead, so the coordinator's access was never
 * actually narrower, only spelled differently. The committed schema does have the
 * role, and `packages/db/src/schema/core/enums.ts` defines it as "takes the
 * report, picks the vendor, chases them, **enters the costs**".
 *
 * Treating it as anything less than management produced two concrete failures
 * before this was corrected: Matt could not open the review queue, the screen
 * built for his job; and he could not advance a single roadside work order,
 * because scoping him to "assigned" left nothing assigned to the person who
 * coordinates all of it.
 */
const MANAGEMENT: UserRole[] = ["admin", "manager", "processor"];

/** Management plus the worker roles. */
const STAFF: UserRole[] = ["admin", "manager", "processor", "mechanic"];

/** Staff plus read-only dispatch. */
const OPERATIONAL: UserRole[] = ["admin", "manager", "processor", "mechanic", "viewer"];

/**
 * Purchase-order approval — the one managerial capability a coordinator does not
 * hold.
 *
 * Not a slight on the role: the documented flow is "Matt or Tony submits ->
 * Brett approves", so submit and approve are deliberately different hands. The
 * handoff is also explicit that **PO approval is a permission, not a person** —
 * it has to survive Brett being on vacation and express limits like "approves up
 * to $5,000", neither of which a role can carry. So when the PO subsystem is
 * built this becomes an approver flag or an approval-authority record and stops
 * being a role list at all. Until then it tracks the two roles that would hold
 * such a flag.
 */
const PO_APPROVERS: UserRole[] = ["admin", "manager"];

/**
 * Who may turn a vendor collector on or off.
 *
 * The same two roles as `PO_APPROVERS` today, and deliberately a separate list
 * rather than a reuse of it: they answer different questions and will diverge.
 * That one becomes an approval-authority record when the PO subsystem lands;
 * this one tracks who is expected to be awake during an incident, which is a
 * fleet manager as much as an administrator — a vendor pull that has started
 * misbehaving should not wait for an admin to be found.
 *
 * The DATABASE is the enforcement point, not this list. `set_collector_settings`
 * gates on the `manage_collectors` row in `routine_permission`, which an admin
 * can widen or narrow from the Settings screen. This matrix decides what the UI
 * offers and gives the API an early, readable refusal; the two are expected to
 * agree and only one of them is authoritative.
 */
const COLLECTOR_OPERATORS: UserRole[] = ["admin", "manager"];

/**
 * Who may switch AI receipt reading off, manual or auto from the Review page.
 *
 * Eden, 1 October 2026: admins and managers. A NEW list, not `MANAGEMENT`,
 * because that includes the processor, and not a reuse of the two lists above,
 * which answer other questions. The database is the enforcement point: the
 * `set_receipt_reading` routine permission, which `receipt-reading.dbtest`
 * holds equal to this list. The parser version and threshold are
 * `configuration` (admin) and live in Settings.
 */
const RECEIPT_READING_SWITCHERS: UserRole[] = ["admin", "manager"];

/**
 * Fleet administration that the coordinator does not do -- currently just
 * creating an asset.
 *
 * Deliberately a SEPARATE constant from `PO_APPROVERS` even though the two hold
 * the same pair today. They are the same list by coincidence, not by rule: PO
 * approval is heading for an approval-authority record with spending limits (see
 * the note above), and when it goes there this must not follow it.
 */
const MANAGER_ONLY: UserRole[] = ["admin", "manager"];

/**
 * Who may close a MaintainX-imported work order without its stage requirements —
 * `closeImportedWorkOrders`, TRBUD-244.
 *
 * A FOURTH constant holding the same pair as `PO_APPROVERS`, `MANAGER_ONLY` and
 * `BUILTIN_REPORT_AUTHORS`, and separate for the reason `MANAGER_ONLY` states:
 * they are the same list by coincidence, not by rule.
 *
 * The coincidence worth noticing is the one that ISN'T here. The obvious reuse
 * would be `cancelWorkOrder`'s `MANAGEMENT`, since both are "finish this record
 * off" powers — but `MANAGEMENT` includes `processor`, and this one must not.
 * Cancelling says "this should never have been raised", which is a coordinator's
 * ordinary judgement; this says "close it as done although none of the evidence
 * the pipeline asks for exists", which is a decision about what the permanent
 * record is allowed to claim.
 *
 * The DATABASE is the enforcement point, not this list:
 * `close_imported_work_order` gates on the `close_imported_work_order` row in
 * `routine_permission`, which an admin can widen. This matrix decides what the UI
 * offers and gives the API an early, readable refusal.
 */
const IMPORT_CLOSERS: UserRole[] = ["admin", "manager"];

/**
 * Who may author a report the whole business runs — `manageBuiltinReports`.
 *
 * A **third** constant holding the same pair as `PO_APPROVERS` and
 * `MANAGER_ONLY`, and separate for the reason stated on `MANAGER_ONLY`: the same
 * list by coincidence, not by rule. Approving a purchase order is heading for an
 * approval-authority record with spending limits; adding a unit to the fleet is
 * fleet administration; and this is a decision about **what the business
 * measures**, which is heading for per-report ownership and sharing (there is no
 * grant table on `report_definition` today, so a built-in is the only report
 * anyone but its author can see).
 *
 * The coordinator is excluded even though he is management everywhere else, and
 * that is the whole point of the capability. Brett: *"some reports shouldn't be
 * custom. It shouldn't be custom reports for me to track how much Clark is
 * costing."* Matt keeps `saveReports`, so nothing stops him building the view he
 * needs — it just lands in his own namespace rather than in the fleet's.
 */
const BUILTIN_REPORT_AUTHORS: UserRole[] = ["admin", "manager"];

/**
 * Who may see where the fleet is right now — `viewLivePositions` (TRBUD-55).
 *
 * **Management plus dispatch**, which is Eden's ruling of 31 August 2026 read
 * literally: `viewer` IS dispatch (see `PERSONAS` in `apps/api/src/session.ts`),
 * and knowing where the trucks are is the substance of that desk's job. So this
 * is `MANAGEMENT` widened by one, not `OPERATIONAL` narrowed by one — the two
 * happen to differ only in `mechanic` today, and writing it as the widening keeps
 * the intent legible if either list moves.
 *
 * ── `mechanic` IS included — Eden's ruling of 9 September 2026 ────────
 *
 * ~~`viewAssets` is `OPERATIONAL` — five of six roles. Live position is a
 * materially different disclosure from the asset register... A mechanic needs
 * the unit in front of them, its history and its parts; they do not need the
 * live location of the other fifty.~~ **Overruled.** Eden, 9 September 2026:
 * *"Mechanics should be able to see the assets page, details, and locations to
 * help them coordinate and plan their work."* Planning where to be and which
 * unit to take next is the mechanic's own job, and location is an input to it.
 *
 * So this list is `MANAGEMENT` widened by `viewer` AND `mechanic`, which today
 * makes it the same membership as `OPERATIONAL`. It is still written out rather
 * than aliased to `OPERATIONAL`, for the reason the note at the bottom of this
 * block gives: a later widening of `viewAssets` — a vendor portal, say — must
 * not drag live position along with it silently.
 *
 * `requester` (a driver) remains excluded, and that exclusion is NOT touched by
 * the ruling above: a driver reading every other driver's position is the
 * disclosure this feature was scoped to avoid, and it is the sharper case. Note
 * `requester` holds neither `viewAssets` nor `viewLivePositions`, so the privacy
 * boundary now coincides with the asset-register boundary instead of sitting
 * inside it.
 *
 * The UAT that prompted this found the two enforcement sites DISAGREEING:
 * `GET /assets` and `/assets/:id` emitted the coordinate to a mechanic while
 * `GET /assets/positions` refused them 403 (TRBUD-212). The ruling settles which
 * side was right — the list — so the fix was to grant the capability rather than
 * to withhold the field.
 *
 * ── A fourth list holding the same pair-plus-one, and separate on purpose ──
 *
 * The same note applies as on `MANAGER_ONLY` and `BUILTIN_REPORT_AUTHORS`: lists
 * that coincide today are not lists that coincide by rule. This one tracks a
 * PRIVACY boundary, and the direction of travel is that it stays where it is
 * while others widen — `viewAssets` widening to a vendor portal, say, must not
 * drag live position along with it.
 */
const POSITION_VIEWERS: UserRole[] = [...MANAGEMENT, "viewer", "mechanic"];

function has(roles: UserRole[], role: UserRole): boolean {
  return roles.includes(role);
}

export function capabilitiesFor(role: UserRole): Capabilities {
  return {
    searchWorkOrders: has(ALL, role),
    // "own WO entries" for the mechanic — the capability is true and the route
    // narrows the rows. See scopeFor("seeCosts", "mechanic").
    seeCosts: has(STAFF, role),
    seeInternalComments:
      has(STAFF, role) || (role === "viewer" && VIEWER_READS_INTERNAL_COMMENTS),
    commentVendorVisible: has(STAFF, role),
    advanceStages: has(STAFF, role),
    createWorkOrder: has(STAFF, role) || role === "requester",
    // TRBUD-442. MANAGEMENT, matching the seeded `decide_maintenance_request`
    // routine key. If the two ever disagree the DATABASE wins.
    decideRequests: has(MANAGEMENT, role),
    editDueDate: has(STAFF, role),
    setWorkOrderPriority: has(MANAGEMENT, role),
    postCosts: has(MANAGEMENT, role),
    postTime: has(STAFF, role),
    confirmInvoice: has(MANAGEMENT, role),
    viewAssets: has(OPERATIONAL, role),
    // Tracks viewAssets in MEMBERSHIP today but is a separate rule, not an
    // alias — see POSITION_VIEWERS above. It stays a distinct capability so a
    // future widening of viewAssets cannot widen live position by accident, and
    // so the map route keeps something of its own to enforce.
    viewLivePositions: has(POSITION_VIEWERS, role),
    markAssetDown: has(STAFF, role),
    correctAssetDowntime: has(MANAGER_ONLY, role),
    // Adding an asset is fleet administration, not shop-floor work: a wrong or
    // duplicate unit becomes the thing every later work order, cost and meter
    // reading hangs off, and `asset` grants no DELETE to the app, so a mistake
    // is corrected by a merge rather than removed.
    //
    // MANAGER_ONLY, not MANAGEMENT -- Eden, 19 August 2026. It shipped as
    // MANAGEMENT first, which included `processor` (the coordinator), and the
    // ruling narrowed it. Admin is retained for the same reason it is retained
    // on purchase-order approval: it is the superuser, not a fourth opinion.
    createAsset: has(MANAGER_ONLY, role),
    viewVendors: has(MANAGEMENT, role),
    viewParts: has(STAFF, role),
    // The inventory room is held by the coordinator and the terminal manager,
    // not by the people who fit the parts — "me and him are the only two people
    // that can pull parts out of the actual inventory room" (Matt Farless).
    // Mechanics keep `viewParts` so they can see what is on the shelf; moving
    // stock and correcting counts is management only (Eden, 18 August 2026).
    //
    // TRBUD-416 keeps it that way. Handing parts to a mechanic, and taking
    // unused ones back, are `issuePart` (decision 23: only Matt or the terminal
    // manager records parts going back). What the mechanic gains is
    // `applyIssuedParts` below: he applies parts already in his hands, so the
    // inventory room still decides what leaves the shelf.
    issuePart: has(MANAGEMENT, role),
    // TRBUD-416. STAFF, matching the seeded `apply_issued_parts` routine key,
    // which is the authority. The mechanic's rows are narrowed to `assigned`
    // in `scopeFor`, and the routine also checks the parts are his.
    applyIssuedParts: has(STAFF, role),
    /*
     * MANAGER_ONLY, not MANAGEMENT -- narrowed by TRBUD-250 line C, and it is a
     * deliberate deviation from the words rather than an oversight. Eden ruled
     * counting open to "admin/managers" on 21 September 2026, and MANAGEMENT
     * includes `processor` (the coordinator), so keeping it here would have been
     * wider than the ruling while looking like it.
     *
     * Free to narrow, and that was checked rather than assumed: this capability
     * had ZERO callers for its whole life -- the Parts row menu's "Adjust count"
     * item was hard-disabled on `disabled: true` and never read it -- so
     * `POST /parts/:id/count` is the first thing it has ever gated.
     *
     * `issuePart` above stays MANAGEMENT. Narrowing THAT would have narrowed
     * issuing, receiving, transferring and reversing with it, which nobody asked
     * for and which would break a processor's day. The database says the same
     * thing one layer down: `count_stock` is its own routine-permission key at
     * {admin, manager} and `move_stock` is untouched at {admin, manager,
     * processor}. Widening either later is one audited `set_routine_permission`
     * call, because permissions are data here -- but this layer would have to be
     * widened with it, which is why the two are worth reading together.
     */
    adjustPartCount: has(MANAGER_ONLY, role),
    // TRBUD-406. MANAGER_ONLY, as `adjustPartCount` above: what each terminal
    // should keep on the shelf is the manager's call. Reading the coverage is
    // `viewParts`, so a mechanic sees the shortfall and cannot change the target.
    manageSpareStock: has(MANAGER_ONLY, role),
    /*
     * TRBUD-342. Read off the two routine keys migration 20261012211859 seeds:
     * `record_inspection` is {admin, manager, processor} (the coordinator does
     * the paperwork; decision 1's default) and `void_inspection` is {admin,
     * manager}. The routine keys are the authority and an administrator can
     * widen them in Settings; these grey the controls, as `adjustPartCount` and
     * `count_stock` already work.
     */
    recordInspection: has(MANAGEMENT, role),
    voidInspection: has(MANAGER_ONLY, role),
    uploadDocuments: has(STAFF, role) || role === "requester",
    // MANAGEMENT, not STAFF. A mechanic may see the photo on a work order and
    // must not see the vendor's invoice for it — see the field's own comment in
    // wire.ts for why reusing `seeCosts` would have done exactly that.
    viewFinancialDocuments: has(MANAGEMENT, role),
    /*
     * The reporting ladder — three rungs, and each one strictly contains the one
     * above it. `reportingLadder` states that as a rule and
     * `permissions.test.ts` asserts it for every role, because a rung a role
     * cannot stand on is worse than no rung: `saveReports` without `viewReports`
     * would be a person who can write a report they cannot then open.
     *
     * 1. viewReports — read the built-ins, run them, export one.
     * 2. saveReports — keep your own definition.
     * 3. manageBuiltinReports — author what the business runs.
     */
    viewReports: has(MANAGEMENT, role),
    reviewMainxExpenses: has(MANAGEMENT, role),
    // Deliberately the SAME list as `viewReports` today, which makes the split
    // look redundant and is not. The rungs differ in what they will do when
    // `viewReports` widens, and widening it is the direction of travel — a
    // mechanic reading his own labour ROI and dispatch reading downtime are both
    // reports the RFP asks for. On that day the reader gains the report and does
    // NOT gain a claim on the shared slug namespace, which is a one-line change
    // here instead of a redesign.
    saveReports: has(MANAGEMENT, role),
    manageBuiltinReports: has(BUILTIN_REPORT_AUTHORS, role),
    approvePurchaseOrders: has(PO_APPROVERS, role),
    // PO_APPROVERS, and it is read off the database rather than chosen here:
    // `routine_permission` holds `record_payment` at {admin, manager}, and both
    // `record_payment` and `allocate_payment` check that key. A capability wider
    // than the routine renders a control that submits and comes back 42501.
    recordVendorPayment: has(PO_APPROVERS, role),
    // Same two roles as PO approval and the collector switches, and a separate
     // list for the same reason those are separate: refusing a supplier is a
     // commercial decision. A coordinator raising work against a vendor is not
     // the same act as deciding the vendor may be used at all.
    manageVendors: has(MANAGER_ONLY, role),
    // Mechanics raise and submit only their own job-linked drafts. Approval,
    // receiving, financial paperwork and settlement remain separate rights.
    createPurchaseOrder: has(STAFF, role),
    configuration: role === "admin",
    manageCollectors: has(COLLECTOR_OPERATORS, role),
    setReceiptReading: has(RECEIPT_READING_SWITCHERS, role),
    viewStatusBoard: has(OPERATIONAL, role),
    detachDocuments: has(MANAGEMENT, role),
    // MANAGEMENT so the matrix agrees with the seeded `verify_document` row. If
    // the two ever disagree the DATABASE wins and this line is what is wrong.
    verifyDocuments: has(MANAGEMENT, role),
    // MANAGER_ONLY, matching the seeded `manage_asset_lifecycle` routine
    // permission. Retiring a unit is fleet administration for the same reason
    // `createAsset` is: it is the thing every later work order hangs off.
    manageAssetLifecycle: has(MANAGER_ONLY, role),
    setLabourRate: has(MANAGER_ONLY, role),
    /*
     * TRBUD-459, PM bonuses. Read off the three routine keys migration
     * 20261030133017 seeds, which are the authority:
     *   viewPmBonuses       MANAGER_ONLY. No routine key: reads are gated
     *                       here and in the route only.
     *   setPmBonusRate      MANAGER_ONLY = `set_pm_bonus_rate`, as labour rates.
     *   correctPmCredit     MANAGER_ONLY = `correct_pm_credit`.
     *   confirmOwnPmCredit  STAFF = `confirm_own_pm_credit`. A mechanic records
     *                       himself on a PM he worked on, and nothing more.
     *
     * MANAGER_ONLY, not MANAGEMENT (Eden, 30 October 2026: "Yes, admin and
     * manager"). MANAGEMENT includes `processor`, and Clark holds mechanic AND
     * processor, so with MANAGEMENT he could see every bonus and credit himself
     * on anyone's PM. On production Brett is admin and Matt is admin and manager
     * (and processor), so both keep the report through those roles.
     */
    viewPmBonuses: has(MANAGER_ONLY, role),
    setPmBonusRate: has(MANAGER_ONLY, role),
    correctPmCredit: has(MANAGER_ONLY, role),
    confirmOwnPmCredit: has(STAFF, role),
    /*
     * TRBUD-467, the AI receipt accuracy page. MANAGER_ONLY (Eden, 30 October
     * 2026: a new view-only permission for admins and managers), deliberately
     * NOT `setReceiptReading`: seeing how the reader does is not switching it.
     * No routine key: it reads, and the route checks it.
     */
    viewReceiptAccuracy: has(MANAGER_ONLY, role),
    cancelWorkOrder: has(MANAGEMENT, role),
    /*
     * TRBUD-244. `IMPORT_CLOSERS`, not `MANAGEMENT` — deliberately narrower than
     * `cancelWorkOrder` right above it, which admits `processor`.
     *
     * Closing a work order without its required evidence is a records-integrity
     * decision, not a coordination one: it writes a permanent
     * `work_order_status_event` asserting the repair is finished history, on the
     * strength of the old system's word. Matching the `close_imported_work_order`
     * row this capability's routine gates on, which migration 0227 seeds as
     * {admin, manager}.
     */
    closeImportedWorkOrders: has(IMPORT_CLOSERS, role),
    /*
     * Admin only, and narrower than `closeImportedWorkOrders` above (Decision
     * 10 of the purchase-order import plan). A manager may approve and cancel a
     * native order; an order imported from MaintainX is history, and retiring one
     * is a records decision rather than a purchasing one.
     */
    cancelImportedPurchaseOrders: role === "admin",
    /*
     * Admin only (Decision 11 of the people-and-comments plan), beside the other
     * overrides on imported history. The routine's own key,
     * `erase_work_order_comment`, is seeded {admin} by migration 0268, so the
     * screen's gate and the database's agree.
     */
    eraseComments: role === "admin",
    /*
     * Maintenance mode (SigmaBuddy's SIGM-205, adapted). Admin only, both halves.
     *
     * SigmaBuddy keeps them as two rights so a developer can work through a
     * window without full production administration. TruckBuddy's developers
     * ARE its admins, and its roles are a fixed matrix rather than custom roles,
     * so the two lists coincide today. They stay two capabilities anyway: the
     * gate reads `bypassSiteMaintenance` and the Settings screen reads
     * `manageSiteMaintenance`, and a later split is then one line here.
     *
     * The containment matters and is asserted in `site-maintenance.test.ts`: whoever
     * can ARM a window must be able to work through it, or arming one would lock
     * its author out of the screen that ends it.
     */
    manageSiteMaintenance: role === "admin",
    bypassSiteMaintenance: role === "admin",
  };
}

/**
 * The reporting rungs, widest first.
 *
 * Written down rather than left implicit so the containment rule is testable:
 * holding rung *n* must imply holding every rung before it. The API relies on it
 * — `POST /reports/definitions` requires `viewReports` **and** the write rung, so
 * a build that broke the ladder would produce a capability nobody could use
 * rather than a hole.
 */
export const reportingLadder: ReadonlyArray<keyof Capabilities> = [
  "viewReports",
  "saveReports",
  "manageBuiltinReports",
];

/**
 * Which capability a report **write** needs, given what it targets.
 *
 * One function rather than a conditional in the route, because the API refuses
 * with it and the builder greys out its Save with it. Two copies of this rule
 * would be two chances for the button and the server to disagree about who may
 * author the fleet's reports.
 */
export function reportWriteCapability(isBuiltin: boolean): keyof Capabilities {
  return isBuiltin ? "manageBuiltinReports" : "saveReports";
}

/**
 * A business name for every capability, for the read-only matrix on the
 * permissions screen.
 *
 * A `Record<keyof Capabilities, string>` on purpose: a capability added to
 * `wire.ts` without a name here fails to compile, which is the same trick
 * `selectionToJson` uses in `reports.ts`. The alternative — a lookup with a
 * fallback — renders `manageBuiltinReports` at an admin and calls it a label.
 */
export const capabilityLabels: Record<keyof Capabilities, string> = {
  searchWorkOrders: "Search work orders",
  seeCosts: "See costs",
  seeInternalComments: "Read internal comments",
  commentVendorVisible: "Post a vendor-visible comment",
  advanceStages: "Advance a work order",
  createWorkOrder: "Create a work order",
  decideRequests: "Convert or decline a maintenance request",
  editDueDate: "Edit work-order details",
  setWorkOrderPriority: "Change work-order priority",
  postCosts: "Post and reverse costs",
  postTime: "Log time",
  confirmInvoice: "Confirm a vendor invoice",
  viewAssets: "View assets",
  viewLivePositions: "See live asset positions on a map",
  markAssetDown: "Mark an asset down",
  correctAssetDowntime: "Correct asset downtime",
  createAsset: "Add an asset to the fleet",
  viewVendors: "View vendors",
  viewParts: "View parts",
  issuePart: "Issue stock to a work order",
  applyIssuedParts: "Apply parts issued to you",
  adjustPartCount: "Correct a stock count",
  manageSpareStock: "Set the spare stock each terminal should hold",
  recordInspection: "Record a DOT inspection",
  voidInspection: "Void a recorded inspection",
  uploadDocuments: "Upload documents",
  viewFinancialDocuments: "Open an invoice or receipt",
  viewReports: "View, run and export reports",
  reviewMainxExpenses: "Review MaintainX expense attribution",
  saveReports: "Save a personal report",
  manageBuiltinReports: "Author a built-in report",
  approvePurchaseOrders: "Approve a purchase order",
  recordVendorPayment: "Record a payment to a vendor",
  manageVendors: "Set a vendor's status",
  createPurchaseOrder: "Raise a purchase order",
  configuration: "Change configuration",
  manageCollectors: "Turn vendor collectors on and off",
  setReceiptReading: "Switch AI receipt reading off, manual or auto",
  viewStatusBoard: "View the status board",
  detachDocuments: "Remove a document from a work order, request, asset or purchase order",
  verifyDocuments: "Verify a receipt or invoice",
  manageAssetLifecycle: "Retire or reactivate an asset",
  setLabourRate: "Set a mechanic's labour rate",
  viewPmBonuses: "See PM bonus counts and totals",
  setPmBonusRate: "Set the PM bonus rate",
  correctPmCredit: "Record or correct who did a PM",
  confirmOwnPmCredit: "Confirm a PM you did",
  viewReceiptAccuracy: "See how accurately the AI reads receipts",
  cancelWorkOrder: "Cancel a work order raised in error",
  closeImportedWorkOrders: "Close a MaintainX-imported work order",
  cancelImportedPurchaseOrders: "Cancel a purchase order imported from MaintainX",
  eraseComments: "Erase a comment's words",
  manageSiteMaintenance: "Take TruckBuddy down for maintenance",
  bypassSiteMaintenance: "Keep working during maintenance",
};

/**
 * The row-level half of the matrix. A capability can be granted while the rows it
 * reaches are narrowed.
 *
 * **Scoping is decided per role, not by "anyone who is not a manager".** An
 * earlier version narrowed every non-managerial role to `assigned`/`own`, which
 * swept `processor` in alongside `mechanic` and made the vendor pipeline
 * undriveable: Matt coordinates every roadside repair and nothing assigns him to
 * them, so "assigned WOs only" left him unable to advance any of the work orders
 * his role exists to move. The distinction that actually matters is
 * **coordinator versus worker**, and BUILD_SPEC's matrix only ever scoped the
 * worker — "assigned WOs" appears on the `mechanic` column, not as a general
 * rule for everyone below manager.
 */
export function scopeFor(capability: keyof Capabilities, role: UserRole): RowScope {
  if (!capabilitiesFor(role)[capability]) return "none";

  switch (role) {
    // Management works across the whole fleet by definition.
    case "admin":
    case "manager":
    case "processor":
      return "all";

    // The worker role: acts on the work in front of them, sees what they posted.
    case "mechanic":
      switch (capability) {
        case "createPurchaseOrder":
          return "own";
        case "advanceStages":
        case "editDueDate":
          return "assigned";
        // TRBUD-416. Parts in his hands go on a job assigned to him or created
        // by him, the same rows as advanceStages; named explicitly so a change
        // to the default arm below cannot widen it.
        case "applyIssuedParts":
          return "assigned";
        // TRBUD-459. Himself only, on a PM he worked on; the routine checks both.
        case "confirmOwnPmCredit":
          return "own";
        case "postTime":
        case "seeCosts":
          // Their own time entries and the costs those produced — not the vendor
          // invoice total, which is a managerial number.
          return "own";
        default:
          return "all";
      }

    // A driver sees their own requests and the work orders they became.
    case "requester":
      return "own";

    // Dispatch reads the fleet and writes nothing; every write capability has
    // already returned "none" above.
    case "viewer":
      return "all";
  }
}

/**
 * A `RowScope` resolved into the two facts a query predicate actually needs.
 *
 * ## Why this exists — TRBUD-86 / TICKET-810
 *
 * Every call site used to write the same idiom:
 *
 *     const own = session.scope("createWorkOrder") === "own";
 *     ... AND (${!own} OR requested_by_user_id = ${session.userId})
 *
 * That collapses a THREE-valued scope into two, and it collapses it the wrong
 * way. `scopeFor` returns `"none"` when the role does not hold the capability at
 * all, so `"none"` and `"all"` both make `own` false and both make `!own` true —
 * **"does not hold it" produces exactly the SQL that "holds it fleet-wide"
 * does.** A viewer holds no `createWorkOrder`, and that is how a session refused
 * a maintenance request was served the request's attachment.
 *
 * So the scope is read POSITIVELY here, once: `"all"` grants, `"own"` narrows,
 * `"none"` denies. A value nobody handled fails to compile rather than defaulting
 * to open.
 *
 * ## `assigned` narrows; it does not grant
 *
 * A deliberate decision rather than an oversight. `"assigned"` means "the work in
 * front of you", and the ownership predicates at the call sites already spell out
 * which relations count as yours — created by you, requested by you, or assigned
 * to you. Treating it as `restrictToUser` therefore narrows to the same user
 * either way, and where a predicate names fewer of those relations than
 * `"assigned"` strictly covers, the result is narrower than the scope allows,
 * never wider. Being too strict is a refusal a person can report; being too
 * loose is this ticket.
 */
export interface RowAccess {
  /**
   * Every row qualifies, so the ownership predicate must not narrow anything.
   * True for `"all"` and for nothing else.
   */
  readonly grantAll: boolean;
  /**
   * The user whose rows qualify, or `null` when **no row does**.
   *
   * A null here is a DENIAL, not the absence of a filter, and it denies by
   * construction: every `column = NULL` comparison evaluates to NULL, which is
   * never true, so a predicate written as
   * `(${grantAll} OR column = ${restrictToUser})` matches nothing. Do not
   * "simplify" a null-valued comparison away — it is the arm that shuts.
   */
  readonly restrictToUser: string | null;
}

/** Read a scope positively. See `RowAccess` for why the negation was unsafe. */
export function rowAccess(scope: RowScope, userId: string): RowAccess {
  switch (scope) {
    case "all":
      return { grantAll: true, restrictToUser: null };
    case "own":
    case "assigned":
      return { grantAll: false, restrictToUser: userId };
    case "none":
      return { grantAll: false, restrictToUser: null };
  }
}

/**
 * Combining several roles held by one person.
 *
 * `user_role` is unique on `(user_id, role) WHERE revoked_at IS NULL`, so one
 * person can hold every role at once — and real people do: Brett is Fleet
 * Manager *and* Admin. Until 18 August 2026 the API resolved that with
 * `ORDER BY granted_at DESC LIMIT 1`, so a second role silently replaced the
 * first and which one won depended on the order somebody happened to grant
 * them. Two roles granted in one transaction share a timestamp, so the answer
 * was not even deterministic.
 *
 * **Most permissive wins** (Eden, 18 August 2026). Union is the only rule that
 * cannot take access away by granting something: adding a role never reduces
 * what a person can do, which is what anyone would expect from "give them
 * Admin as well".
 */
export function capabilitiesForRoles(roles: readonly UserRole[]): Capabilities {
  // Start from a role that holds nothing, then turn switches on. `viewer` is not
  // a safe base — it holds read capabilities — so build the base explicitly.
  const base = capabilitiesFor("viewer");
  const out = Object.fromEntries(
    Object.keys(base).map((k) => [k, false]),
  ) as unknown as Capabilities;

  for (const role of roles) {
    const can = capabilitiesFor(role);
    for (const key of Object.keys(out) as (keyof Capabilities)[]) {
      if (can[key]) out[key] = true;
    }
  }
  return out;
}

/**
 * Rank, so "most permissive" is defined rather than assumed.
 *
 * `assigned` above `own` is a judgement: they narrow on different axes (work
 * assigned to you, versus rows you created), so neither strictly contains the
 * other. It is safe in practice because the only scope that changes behaviour
 * across a union is `all`, and no seeded role pairs `own` and `assigned` on the
 * same capability. If one ever does, revisit this rather than widening it.
 */
const SCOPE_RANK: Record<RowScope, number> = { none: 0, own: 1, assigned: 2, all: 3 };

/** The widest scope any of the held roles grants for this capability. */
export function scopeForRoles(
  capability: keyof Capabilities,
  roles: readonly UserRole[],
): RowScope {
  let widest: RowScope = "none";
  for (const role of roles) {
    const scope = scopeFor(capability, role);
    if (SCOPE_RANK[scope] > SCOPE_RANK[widest]) widest = scope;
  }
  return widest;
}

/**
 * Most-privileged first. Used only for presentation — the landing screen, the
 * persona label, and the role named in a refusal message. **Never for deciding
 * a capability**: that is `capabilitiesForRoles`, which reads every role.
 */
const ROLE_PRECEDENCE: readonly UserRole[] = [
  "admin",
  "manager",
  "processor",
  "mechanic",
  "requester",
  "viewer",
];

/** The role to show for someone holding several. Presentation only. */
export function primaryRoleOf(roles: readonly UserRole[]): UserRole {
  for (const candidate of ROLE_PRECEDENCE) {
    if (roles.includes(candidate)) return candidate;
  }
  return "viewer";
}

/* ── Role preview: narrowing only (TRBUD-253) ──────────────────────── */

/**
 * The capability set a session sees while PREVIEWING another role.
 *
 * The whole safety property of TRBUD-253 lives in this function, so it is written
 * to be read rather than folded into the session resolver. `apps/api/src/session.ts`
 * opens with the rule the persona header broke — a caller who can choose their own
 * role has chosen their own permissions, which is why B4 says "the role switcher
 * must not ship". A preview is safe exactly because it is an **intersection**:
 *
 *     effective = capabilitiesForRoles([preview]) AND capabilitiesForRoles(real)
 *
 * so a mechanic previewing `admin` gets the mechanic's set unchanged, and there is
 * no role, no sequence of previews and no combination of held roles that produces
 * a capability the account did not already have. The other half — who may name a
 * role at all — is decided in the database by `tb_auth_set_preview_role` against
 * `user_role`, not here and not by the client.
 *
 * A null preview returns the real set, which is what makes "Exit preview" free.
 */
export function narrowedCapabilities(
  realRoles: readonly UserRole[],
  previewRole: UserRole | null,
): Capabilities {
  const real = capabilitiesForRoles(realRoles);
  if (!previewRole) return real;
  const preview = capabilitiesFor(previewRole);
  const out = { ...real };
  for (const key of Object.keys(out) as (keyof Capabilities)[]) {
    out[key] = real[key] && preview[key];
  }
  return out;
}

/**
 * The row scope a session gets while previewing — the NARROWER of the two.
 *
 * `scopeForRoles` widens across held roles ("most permissive wins"); this takes the
 * minimum against the previewed role, so an admin previewing `mechanic` picks up
 * the mechanic's `assigned` scope on the queue and sees only work assigned to
 * them. That is the visible consequence of previewing and is the point of it: a
 * preview that kept `all` would show the admin's queue wearing a mechanic's label,
 * which is precisely the lie a UI-only preview tells.
 *
 * A capability the preview does not hold at all resolves to `none` here, which
 * agrees with `narrowedCapabilities` turning the boolean off.
 */
export function narrowedScopeFor(
  capability: keyof Capabilities,
  realRoles: readonly UserRole[],
  previewRole: UserRole | null,
): RowScope {
  const real = scopeForRoles(capability, realRoles);
  if (!previewRole) return real;
  const preview = scopeFor(capability, previewRole);
  return SCOPE_RANK[preview] < SCOPE_RANK[real] ? preview : real;
}

/**
 * Which roles this account may preview.
 *
 * An admin may preview any role — that is the point of the feature for Eden, who
 * needs to see what Clark sees. Anyone else may preview only a role they already
 * hold, which for the common single-role account is an empty offer and no control.
 * The account's OWN roles are excluded from nobody: previewing a role you hold
 * alongside others is the useful case (Brett is manager *and* admin, and wants to
 * see the manager-only app).
 *
 * Mirrors the check inside `tb_auth_set_preview_role`. The database is the gate;
 * this decides what the screen offers, so a control is never rendered for
 * something the routine would refuse.
 */
export function previewableRoles(realRoles: readonly UserRole[]): UserRole[] {
  if (realRoles.includes("admin")) return [...ROLE_PRECEDENCE];
  return ROLE_PRECEDENCE.filter((role) => realRoles.includes(role));
}

/** The screen a role lands on. Personas map onto roles, not the other way. */
export function defaultRouteFor(role: UserRole): string {
  switch (role) {
    case "manager":
      // Reports is the sponsor's preference but has the least backing. The
      // handoff's own sequencing note offers Repair overview as the
      // alternative; Reports wins here because the gold views now cover its
      // KPI cards, and the two untrustworthy numbers carry caveats.
      return "/reports";
    case "processor":
      return "/overview";
    case "mechanic":
      return "/work-orders";
    case "viewer":
      return "/board";
    case "requester":
      return "/requests";
    case "admin":
      return "/work-orders";
  }
}

/** Tabs the role may see. `requester` gets no tab bar at all. */
export function visibleTabsFor(role: UserRole): string[] {
  if (role === "requester") return [];
  return visibleNavigationItems([role], capabilitiesFor(role)).map((item) => item.path);
}
